import { DdLogs } from '@/modules/datadog'

type IdentityUser = {
  _id: string
  auth0Id?: string | null
  createdAt?: string | number | Date | null
}

const NEW_USER_WINDOW_MS = 24 * 60 * 60 * 1000

export function toUnixSeconds(date: IdentityUser['createdAt']): number | undefined {
  if (!date) return undefined
  const ms = new Date(date).getTime()
  return Number.isNaN(ms) ? undefined : Math.round(ms / 1000)
}

// Auth0 is the canonical analytics id; the Mongo id travels as a trait so the
// Customer.io destination function keeps `mongo_user_id` correct.
export function toAnalyticsIdentity(user: IdentityUser) {
  if (!user.auth0Id) {
    DdLogs.warn(`[analytics] user ${user._id} has no auth0Id, identifying with Mongo id`)
  }
  const createdAtSeconds = toUnixSeconds(user.createdAt)
  return {
    userId: user.auth0Id || user._id,
    traits: {
      mongoUserId: user._id,
      ...(user.auth0Id && { auth0Id: user.auth0Id }),
      ...(createdAtSeconds !== undefined && { user_created_at: createdAtSeconds })
    }
  }
}

// Alias only while Segment still holds another id (anonymous or legacy Mongo);
// an Auth0 → Auth0 alias would be a pointless self-merge in Customer.io.
export function shouldAliasToAuth0(
  auth0Id: string | null | undefined,
  currentSegmentUserId?: string | null
): auth0Id is string {
  return !!auth0Id && auth0Id !== currentSegmentUserId
}

// `isUserNew` actually stores onboarding completion (true = completed).
export function getLoginLifecycleProps(user: IdentityUser, isUserNew: boolean | undefined, now = Date.now()) {
  const createdAtSeconds = toUnixSeconds(user.createdAt)
  const isNew = createdAtSeconds !== undefined && now - createdAtSeconds * 1000 < NEW_USER_WINDOW_MS
  return {
    user_type: isNew ? 'new' : 'returning',
    user_onboarding_status: isUserNew === true ? 'completed' : 'incomplete',
    ...(createdAtSeconds !== undefined && { user_created_at: createdAtSeconds })
  }
}
